import React, { useState, useEffect } from "react";
import {
  Header,
  Form,
  Button,
  Grid,
  Divider,
  Icon,
  Dropdown,
} from "semantic-ui-react";
import { invenioConfig } from "@config";
import { configurableApi } from "@api/configurables/configurables";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import DocumentUsageBreakdown from "@modules/Stats/DocumentUsage/DocumentUsageBreakdown";

const getCsrfToken = () => {
  const cookies = document.cookie.split(";");
  const csrfCookie = cookies.find((c) => c.trim().startsWith("csrftoken="));
  if (csrfCookie) {
    const token = csrfCookie.split("=")[1];
    return token;
  }

  const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (metaToken) {
    return metaToken;
  }

  return null;
};

const formatSectionLabel = (sectionValue) => {
  const labelMap = {
    Graduate_Thesis: "Graduate Thesis",
    Undergraduate_Thesis: "Undergraduate Thesis",
  };
  return labelMap[sectionValue] || sectionValue;
};

const CustomReportGenerator = () => {
  const [formData, setFormData] = useState({
    academicYear: "",
    semester: "",
    sections: [],
    transactionType: "both",
    userTypes: [],
    yearLevels: [],
    programs: [],
    departments: [],
  });

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [availableUserTypes, setAvailableUserTypes] = useState([]);
  const [availableYearLevels, setAvailableYearLevels] = useState([]);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    initializeForm();
  }, []);

  const initializeForm = async () => {
    await fetchCurrentAcademicYearFromConfigurables();
    fetchAvailableSections();
    fetchAdvancedFilters();
  };

  const fetchCurrentAcademicYearFromConfigurables = async () => {
    const configurablesResponse = await configurableApi.getAll();

    if (configurablesResponse && configurablesResponse.data) {
      const configurables =
        configurablesResponse.data.hits || configurablesResponse.data || [];
      const currentAYConfig = configurables.find(
        (c) => c.name === "current_academic_year"
      );

      if (currentAYConfig && currentAYConfig.value) {
        const currentAcademicYearValue = currentAYConfig.value.trim();
        await fetchAcademicOptions(currentAcademicYearValue);
      } else {
        await fetchAcademicOptions();
      }
    } else {
      await fetchAcademicOptions();
    }
  };

  const fetchAdvancedFilters = async () => {
    const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;

    const [userTypesRes, yearLevelsRes, programsRes, departmentsRes] =
      await Promise.all([
        fetch(`${baseURL}/document-usage/user-types`),
        fetch(`${baseURL}/document-usage/year-levels`),
        fetch(`${baseURL}/document-usage/programs`),
        fetch(`${baseURL}/document-usage/departments`),
      ]);

    if (userTypesRes.ok) {
      const userTypesResult = await userTypesRes.json();
      if (userTypesResult.status === "success") {
        setAvailableUserTypes(userTypesResult.data);
      }
    }

    if (yearLevelsRes.ok) {
      const yearLevelsResult = await yearLevelsRes.json();
      if (yearLevelsResult.status === "success") {
        setAvailableYearLevels(yearLevelsResult.data);
      }
    }

    if (programsRes.ok) {
      const programsResult = await programsRes.json();
      if (programsResult.status === "success") {
        setAvailablePrograms(programsResult.data);
      }
    }

    if (departmentsRes.ok) {
      const departmentsResult = await departmentsRes.json();
      if (departmentsResult.status === "success") {
        setAvailableDepartments(departmentsResult.data);
      }
    }
  };

  const fetchAvailableSections = async () => {
    const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
    const response = await fetch(`${baseURL}/document-usage/sections`);

    if (response.ok) {
      const result = await response.json();
      if (result.status === "success") {
        setAvailableSections(result.data);
      }
    }
  };

  const fetchAcademicOptions = async (currentAcademicYearFromConfig = null) => {
    const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
    const yearsResponse = await fetch(`${baseURL}/document-usage/years`);

    if (yearsResponse.ok) {
      const yearsResult = await yearsResponse.json();
      if (yearsResult.status === "success") {
        const formattedYears = yearsResult.data.map((year) => ({
          key: year.id,
          text: year.name,
          value: year.id,
        }));

        setAcademicYearOptions(formattedYears);

        let selectedYear = null;

        if (currentAcademicYearFromConfig) {
          selectedYear = formattedYears.find(
            (year) =>
              year.text === currentAcademicYearFromConfig ||
              year.value === currentAcademicYearFromConfig
          );
        }

        if (!selectedYear) {
          selectedYear = formattedYears.find((year) => year.current);
        }

        if (selectedYear) {
          setFormData((prev) => ({
            ...prev,
            academicYear: selectedYear.value,
          }));
          await fetchSemesters(selectedYear.value);
        }
      }
    }
  };

  const fetchSemesters = async (academicYear) => {
    const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
    const response = await fetch(
      `${baseURL}/document-usage/semesters?academic_year=${academicYear}`
    );

    if (response.ok) {
      const result = await response.json();
      if (result.status === "success") {
        const formattedSemesters = result.data.map((semester) => ({
          key: `${academicYear}-${semester.id}`,
          text: semester.name,
          value: semester.value,
          period_id: semester.period_id,
        }));

        setSemesterOptions(formattedSemesters);

        const currentSemester = formattedSemesters.find((sem) => sem.current);
        if (currentSemester) {
          setFormData((prev) => ({
            ...prev,
            semester: currentSemester.value,
          }));
        }
      }
    } else {
      setSemesterOptions([]);
    }
  };

  const sectionLabels = availableSections.reduce((acc, section) => {
    acc[section.value] = formatSectionLabel(section.name);
    return acc;
  }, {});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "academicYear") {
      fetchSemesters(value);
      setFormData((prev) => ({ ...prev, semester: "" }));
    }
  };

  const handleMultiSelectChange = (field, values) => {
    setFormData((prev) => ({
      ...prev,
      [field]: values || [],
    }));
  };

  const handleUserTypeChange = (values) => {
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        userTypes: values || [],
      };

      if (values && values.includes("faculty")) {
        newFormData.yearLevels = [];
      }

      return newFormData;
    });
  };

  const checkFiltersApplied = () => {
    const hasFilters =
      formData.userTypes.length > 0 ||
      formData.yearLevels.length > 0 ||
      formData.programs.length > 0 ||
      formData.departments.length > 0 ||
      formData.sections.length > 0;
    setFiltersApplied(hasFilters);
    return hasFilters;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const apiData = {
      academic_year: formData.academicYear,
      semester: formData.semester,
      sections: formData.sections,
      transactionType: formData.transactionType,
      userTypes: formData.userTypes,
      yearLevels: formData.yearLevels,
      programs: formData.programs,
      departments: formData.departments,
    };

    const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;

    const csrfToken = getCsrfToken();

    const headers = {
      "Content-Type": "application/json",
    };

    if (csrfToken) {
      headers["X-CSRFToken"] = csrfToken;
    }

    const response = await fetch(`${baseURL}/document-usage/report`, {
      method: "POST",
      headers: headers,
      credentials: "include",
      body: JSON.stringify(apiData),
    });

    if (response.ok) {
      const result = await response.json();
      if (result.status === "success") {
        setReportData(result);
        checkFiltersApplied();
      } else {
        console.error("API error:", result.message);
      }
    } else {
      const errorText = await response.text();
      console.error("Server error response:", errorText);
    }

    setLoading(false);
  };

  const handleReset = () => {
    setFormData({
      academicYear: "",
      semester: "",
      sections: [],
      transactionType: "both",
      userTypes: [],
      yearLevels: [],
      programs: [],
      departments: [],
    });
    setReportData(null);
    setFiltersApplied(false);
    initializeForm();
  };

  const exportToPDF = () => {
    if (!reportData) return;

    const summaryTableHeaders = `
      <th>Library Section</th>
      <th>Checkouts</th>
      <th>Returns</th>
      <th>Total</th>
      <th>Usage %</th>
    `;

    const summaryTableRows = Object.entries(reportData.data?.sections || {})
      .map(([section, data]) => {
        const total = data.checkouts + data.returns;
        const grandTotal =
          (reportData.data?.totals?.checkouts || 0) +
          (reportData.data?.totals?.returns || 0);
        const percentage =
          grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : 0;

        return `
          <tr>
            <td><strong>${sectionLabels[section] || section}</strong></td>
            <td style="text-align: center;">${data.checkouts}</td>
            <td style="text-align: center;">${data.returns}</td>
            <td style="text-align: center;"><strong>${total}</strong></td>
            <td style="text-align: center;">${percentage}%</td>
          </tr>
        `;
      })
      .join("");

    const totalCheckouts = reportData.data?.totals?.checkouts || 0;
    const totalReturns = reportData.data?.totals?.returns || 0;
    const grandTotal = totalCheckouts + totalReturns;

    const summaryTotalsRow = `
      <tr style="background-color: #f5f5f5; font-weight: bold;">
        <td><strong>TOTAL</strong></td>
        <td style="text-align: center;"><strong>${totalCheckouts}</strong></td>
        <td style="text-align: center;"><strong>${totalReturns}</strong></td>
        <td style="text-align: center;"><strong>${grandTotal}</strong></td>
        <td style="text-align: center;"><strong>100%</strong></td>
      </tr>
    `;

    let breakdownTableRows = "";
    if (filtersApplied && reportData.data?.detailed_breakdown) {
      const detailRows = [];

      Object.entries(reportData.data.detailed_breakdown).forEach(
        ([section, demographics]) => {
          if (
            demographics.by_user_type &&
            Object.keys(demographics.by_user_type).length > 0
          ) {
            let isFirstSectionRow = true;

            Object.entries(demographics.by_user_type).forEach(
              ([userType, userData]) => {
                const userTotal = userData.checkouts + userData.returns;

                if (
                  !userData.programs ||
                  Object.keys(userData.programs).length === 0
                ) {
                  detailRows.push(`
                <tr>
                  <td><strong>${
                    isFirstSectionRow ? sectionLabels[section] || section : ""
                  }</strong></td>
                  <td>${userType}</td>
                  <td style="text-align: center;">-</td>
                  <td style="text-align: center;">-</td>
                  <td style="text-align: center;"><strong>${userTotal}</strong></td>
                </tr>
              `);
                  isFirstSectionRow = false;
                } else {
                  let isFirstUserTypeRow = true;

                  Object.entries(userData.programs).forEach(
                    ([program, progData]) => {
                      if (
                        !progData.year_levels ||
                        Object.keys(progData.year_levels).length === 0
                      ) {
                        const progTotal = progData.checkouts + progData.returns;
                        detailRows.push(`
                    <tr>
                      <td><strong>${
                        isFirstSectionRow
                          ? sectionLabels[section] || section
                          : ""
                      }</strong></td>
                      <td>${isFirstUserTypeRow ? userType : ""}</td>
                      <td>${program}</td>
                      <td style="text-align: center;">-</td>
                      <td style="text-align: center;"><strong>${progTotal}</strong></td>
                    </tr>
                  `);
                        isFirstSectionRow = false;
                        isFirstUserTypeRow = false;
                      } else {
                        let isFirstProgramRow = true;

                        Object.entries(progData.year_levels).forEach(
                          ([yearLevel, yearData]) => {
                            const yearTotal =
                              yearData.checkouts + yearData.returns;
                            detailRows.push(`
                      <tr>
                        <td><strong>${
                          isFirstSectionRow
                            ? sectionLabels[section] || section
                            : ""
                        }</strong></td>
                        <td>${isFirstUserTypeRow ? userType : ""}</td>
                        <td>${isFirstProgramRow ? program : ""}</td>
                        <td>${yearLevel}</td>
                        <td style="text-align: center;"><strong>${yearTotal}</strong></td>
                      </tr>
                    `);
                            isFirstSectionRow = false;
                            isFirstUserTypeRow = false;
                            isFirstProgramRow = false;
                          }
                        );
                      }
                    }
                  );
                }
              }
            );
          }
        }
      );

      if (detailRows.length > 0) {
        breakdownTableRows = detailRows.join("");
      }
    }

    let combinedTableRows = summaryTableRows + summaryTotalsRow;

    if (breakdownTableRows) {
      const breakdownHeaders = `
        <th>Section</th>
        <th>User Type</th>
        <th>Program</th>
        <th>Year Level</th>
        <th>Total</th>
      `;

      combinedTableRows += `
        </tbody>
        </table>
        
        <h3 style="margin-top: 30px; color: #2c3e50; page-break-before: auto;">Detailed User Demographics Breakdown</h3>
        <p style="color: #666; margin-bottom: 15px;">Specific breakdown by programs and year levels.</p>
        
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <thead>
          <tr>${breakdownHeaders}</tr>
        </thead>
        <tbody>
          ${breakdownTableRows}
        </tbody>
      `;
    }

    const html = generateExportHTML({
      reportTitle: `Document Usage Report - ${reportData.period}`,
      generatedDate: new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      tableHeaders: summaryTableHeaders,
      tableRows: combinedTableRows,
      reportType: "documentusage",
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const exportToCSV = () => {
    if (!reportData) return;

    let csvContent = "";

    csvContent += `Document Usage Report - ${reportData.period}\n`;
    csvContent += `Generated on: ${new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}\n\n`;

    const appliedFiltersInfo = [];
    if (formData.sections.length > 0) {
      appliedFiltersInfo.push(
        `Sections: ${formData.sections.map((s) => sectionLabels[s]).join(", ")}`
      );
    }
    appliedFiltersInfo.push(`Transaction Type: ${formData.transactionType}`);
    if (formData.userTypes.length > 0) {
      appliedFiltersInfo.push(`User Types: ${formData.userTypes.join(", ")}`);
    }
    if (formData.yearLevels.length > 0) {
      appliedFiltersInfo.push(`Year Levels: ${formData.yearLevels.join(", ")}`);
    }
    if (formData.programs.length > 0) {
      appliedFiltersInfo.push(`Programs: ${formData.programs.join(", ")}`);
    }
    if (formData.departments.length > 0) {
      appliedFiltersInfo.push(
        `Departments: ${formData.departments.join(", ")}`
      );
    }

    csvContent += "Applied Filters:\n";
    appliedFiltersInfo.forEach((filter) => {
      csvContent += `${filter}\n`;
    });
    csvContent += "\n";

    csvContent += "USAGE SUMMARY BY SECTION\n";
    csvContent += "Library Section,Checkouts,Returns,Total,Usage %\n";

    const summaryRows = Object.entries(reportData.data?.sections || {}).map(
      ([section, data]) => {
        const total = data.checkouts + data.returns;
        const grandTotal =
          (reportData.data?.totals?.checkouts || 0) +
          (reportData.data?.totals?.returns || 0);
        const percentage =
          grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : 0;

        return [
          sectionLabels[section] || section,
          data.checkouts,
          data.returns,
          total,
          `${percentage}%`,
        ];
      }
    );

    summaryRows.forEach((row) => {
      csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    const totalCheckouts = reportData.data?.totals?.checkouts || 0;
    const totalReturns = reportData.data?.totals?.returns || 0;
    const grandTotal = totalCheckouts + totalReturns;
    csvContent += `"TOTAL","${totalCheckouts}","${totalReturns}","${grandTotal}","100%"\n\n`;

    if (filtersApplied && reportData.data?.detailed_breakdown) {
      csvContent += "DETAILED USER DEMOGRAPHICS BREAKDOWN\n";
      csvContent += "Section,User Type,Program,Year Level,Total\n";

      Object.entries(reportData.data.detailed_breakdown).forEach(
        ([section, demographics]) => {
          if (
            demographics.by_user_type &&
            Object.keys(demographics.by_user_type).length > 0
          ) {
            Object.entries(demographics.by_user_type).forEach(
              ([userType, userData]) => {
                const userTotal = userData.checkouts + userData.returns;

                if (
                  !userData.programs ||
                  Object.keys(userData.programs).length === 0
                ) {
                  csvContent += `"${
                    sectionLabels[section] || section
                  }","${userType}","-","-","${userTotal}"\n`;
                } else {
                  Object.entries(userData.programs).forEach(
                    ([program, progData]) => {
                      if (
                        !progData.year_levels ||
                        Object.keys(progData.year_levels).length === 0
                      ) {
                        const progTotal = progData.checkouts + progData.returns;
                        csvContent += `"${
                          sectionLabels[section] || section
                        }","${userType}","${program}","-","${progTotal}"\n`;
                      } else {
                        Object.entries(progData.year_levels).forEach(
                          ([yearLevel, yearData]) => {
                            const yearTotal =
                              yearData.checkouts + yearData.returns;
                            csvContent += `"${
                              sectionLabels[section] || section
                            }","${userType}","${program}","${yearLevel}","${yearTotal}"\n`;
                          }
                        );
                      }
                    }
                  );
                }
              }
            );
          }
        }
      );
    }

    if (reportData.data?.summary_message) {
      csvContent += "\nREPORT SUMMARY\n";
      csvContent += `"${reportData.data.summary_message.replace(
        /"/g,
        '""'
      )}"\n`;
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `document-usage-report-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isYearLevelDisabled = formData.userTypes.includes("faculty");

  const sectionOptions = availableSections.map((section) => ({
    key: section.value,
    text: formatSectionLabel(section.name),
    value: section.value,
  }));

  const userTypeOptions = availableUserTypes.map((type) => ({
    key: type.value,
    text: type.name,
    value: type.value,
  }));

  const yearLevelOptions = availableYearLevels.map((level) => ({
    key: level.value,
    text: level.name,
    value: level.value,
  }));

  const programOptions = availablePrograms.map((program) => ({
    key: program.value,
    text: program.name,
    value: program.value,
  }));

  const departmentOptions = availableDepartments.map((dept) => ({
    key: dept.value,
    text: dept.name,
    value: dept.value,
  }));

  return (
    <div>
      <>
        <Header as="h3" color="green">
          Generate Custom Report
        </Header>

        <Form onSubmit={handleSubmit}>
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Dropdown
                  label="Academic Year"
                  placeholder="Select Academic Year"
                  options={academicYearOptions}
                  value={formData.academicYear}
                  onChange={(e, { value }) =>
                    handleInputChange("academicYear", value)
                  }
                  selection
                  required
                  fluid
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Dropdown
                  label="Semester"
                  placeholder="Select Semester"
                  options={semesterOptions}
                  value={formData.semester}
                  onChange={(e, { value }) =>
                    handleInputChange("semester", value)
                  }
                  selection
                  required
                  fluid
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={3}>
              <Grid.Column>
                <Form.Dropdown
                  label="Library Sections"
                  placeholder="Select Sections"
                  required
                  fluid
                  multiple
                  selection
                  options={sectionOptions}
                  value={formData.sections}
                  onChange={(e, { value }) =>
                    handleMultiSelectChange("sections", value)
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Transaction Type</label>
                  <Dropdown
                    placeholder="Select Transaction Type"
                    fluid
                    selection
                    options={[
                      {
                        key: "both",
                        text: "Both (Checkout & Return)",
                        value: "both",
                      },
                      {
                        key: "checkout",
                        text: "Checkout Only",
                        value: "checkout",
                      },
                      { key: "return", text: "Return Only", value: "return" },
                    ]}
                    value={formData.transactionType}
                    onChange={(e, { value }) =>
                      handleInputChange("transactionType", value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>User Types</label>
                  <Dropdown
                    placeholder="Select User Types"
                    fluid
                    multiple
                    selection
                    options={userTypeOptions}
                    value={formData.userTypes}
                    onChange={(e, { value }) => handleUserTypeChange(value)}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row columns={3}>
              <Grid.Column>
                <Form.Field disabled={isYearLevelDisabled}>
                  <label>
                    Year Levels
                    {isYearLevelDisabled && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "0.85em",
                          color: "black",
                          fontWeight: "normal",
                        }}
                      >
                        (Not applicable for Faculty)
                      </span>
                    )}
                  </label>
                  <Dropdown
                    placeholder={
                      isYearLevelDisabled
                        ? "Not applicable for Faculty"
                        : "Select Year Levels"
                    }
                    fluid
                    multiple
                    selection
                    options={yearLevelOptions}
                    value={formData.yearLevels}
                    onChange={(e, { value }) =>
                      handleMultiSelectChange("yearLevels", value)
                    }
                    disabled={isYearLevelDisabled}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Programs</label>
                  <Dropdown
                    placeholder="Select Programs"
                    fluid
                    multiple
                    selection
                    options={programOptions}
                    value={formData.programs}
                    onChange={(e, { value }) =>
                      handleMultiSelectChange("programs", value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Departments</label>
                  <Dropdown
                    placeholder="Select Departments"
                    fluid
                    multiple
                    selection
                    options={departmentOptions}
                    value={formData.departments}
                    onChange={(e, { value }) =>
                      handleMultiSelectChange("departments", value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <Divider />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button type="button" onClick={handleReset}>
                <Icon name="refresh" />
                Clear Filters
              </Button>
              <Button
                type="submit"
                primary
                loading={loading}
                disabled={
                  !formData.academicYear ||
                  !formData.semester ||
                  formData.sections.length === 0
                }
              >
                Generate Report
              </Button>
            </div>
          </div>
        </Form>
      </>

      <DocumentUsageBreakdown
        reportData={reportData}
        formData={formData}
        sectionLabels={sectionLabels}
        onExportPDF={exportToPDF}
        onExportCSV={exportToCSV}
        filtersApplied={filtersApplied}
      />
    </div>
  );
};

export default CustomReportGenerator;
